import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withSessionRedirect } from '../lib/require-session.ts'
import { VaultApprovals } from '../components/VaultApprovals.tsx'
import { fetchHierarchy } from '../lib/hierarchy-api.ts'
import { getTenantKey, listApprovals } from '../lib/vault-api.ts'
import { movedTrail, trailParam } from '../lib/cursor-trail.ts'
import { useAutoRefresh } from '../lib/use-auto-refresh.ts'
import {
  DEFAULT_APPROVAL_PAGE_SIZE,
  parseApprovalsSearch,
  resolveApprovalsView,
} from '../lib/vault-approvals-search.ts'

export const Route = createFileRoute('/vault/approvals')({
  validateSearch: parseApprovalsSearch,
  loaderDeps: ({ search }) => ({
    tab: search.tab,
    pageSize: search.pageSize,
    cursors: search.cursors,
  }),
  loader: async ({ deps }) =>
    withSessionRedirect(async () => {
      const tree = await fetchHierarchy()
      const rootId = tree.roots[0]?.id ?? ''
      if (!rootId)
        return {
          rootId,
          rows: [],
          nextBefore: null,
          tenantPubkey: null,
          currentKekVersion: null,
        }
      const view = resolveApprovalsView(deps)
      // The tenant key is fetched for one reason: the "supply a one-off
      // value" answer seals in the browser, and sealing needs the PUBLIC
      // half. It deliberately does not need an unlock, so this is a plain
      // read alongside the queue rather than anything gated.
      const [page, tenantKey] = await Promise.all([
        listApprovals(rootId, {
          state: view.tab,
          limit: view.pageSize,
          before: view.before,
        }),
        getTenantKey(rootId),
      ])
      return {
        rootId,
        rows: page.requests,
        nextBefore: page.nextBefore,
        tenantPubkey: tenantKey.tenantPubkey,
        currentKekVersion: tenantKey.currentKekVersion,
      }
    }),
  component: VaultApprovalsRoute,
})

/** Matches Task 38's spec: the approvals queue is where an operator is
 * expected to unblock a waiting agent, so it refreshes faster than the
 * daemon enrolment table. */
const REFRESH_MS = 10_000

function VaultApprovalsRoute() {
  const { rootId, rows, nextBefore, tenantPubkey, currentKekVersion } =
    Route.useLoaderData()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const router = useRouter()
  const view = resolveApprovalsView(search)
  useAutoRefresh(REFRESH_MS)

  /** Rewrites the search params, keeping the ones this change does not
   * speak to. `replace`, because every one of these is an adjustment to
   * the same view and pushing a dozen of them would turn Back into an
   * undo for tab clicks instead of a way off the page. */
  function go(next: Partial<typeof search>): void {
    void navigate({ search: { ...search, ...next }, replace: true })
  }

  return (
    <VaultApprovals
      rootId={rootId}
      rows={rows}
      tab={view.tab}
      page={view.page}
      pageSize={view.pageSize}
      // The cursor, not a count: this list has no total.
      hasNextPage={nextBefore !== null}
      tenantPubkey={tenantPubkey}
      currentKekVersion={currentKekVersion}
      onTabChange={(tab) => {
        // Back to page 1. The trail was walked through one state's rows,
        // so its cursors are positions in a list that no longer exists.
        go({ tab, cursors: undefined })
      }}
      onPageSizeChange={(pageSize) => {
        // Same reasoning: a cursor is a position in a walk of a given
        // stride, so changing the stride invalidates it. The default is
        // left out of the URL rather than written into it — a param that
        // only restates the default is noise in a pasted link.
        go({
          pageSize:
            pageSize === DEFAULT_APPROVAL_PAGE_SIZE ? undefined : pageSize,
          cursors: undefined,
        })
      }}
      onPageChange={(wanted) => {
        const trail = movedTrail(view, wanted, nextBefore)
        if (trail === undefined) return
        go({ cursors: trailParam(trail) })
      }}
      onChanged={async () => {
        await router.invalidate()
      }}
    />
  )
}
